import React, {
  useCallback,
  useState,
} from 'react'
import {
  shallowEqual,
  useSelector,
} from 'react-redux'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router-dom'

import { validate } from 'lib/validation'

import type { ApiResponse } from 'utils/apis/insuranceLounge'
import { post } from 'utils/apis/insuranceLounge'

import type { RootState } from 'state/types'
import { getQuoteRequestValue } from 'state/quotes/selectors'

import {
  events,
  useGoogleTagManager,
} from 'hooks/useGoogleTagManager'

import Content from 'components/base/Content'
import Title from 'components/base/Title'
import ButtonRow from 'components/base/Button/Row'
import Button from 'components/base/Button'
import Form from 'components/base/Form'
import HeaderMeta from 'components/HeaderMeta'
import { Type as InputType } from 'components/base/Form/Input'
import type { Value } from 'components/base/Form/Input'
import InputGroup from 'components/base/Form/InputGroup'
import SelectOption from 'components/base/Form/Input/Select/Option'
import QuotingWrapper, { Theme as QuotingWrapperTheme } from 'components/blocks/QuotingWrapper'
import UserSuppliedText from 'components/base/UserSuppliedText'

import { Props } from './types'

const QuotingPersonalInfo: React.FC<Props> = ({ insuranceType }): React.ReactElement => {
  const history = useHistory()

  const [
    isSubmitting,
    setIsSubmitting,
  ] = useState(false)

  const firstName = useSelector((state: RootState) => getQuoteRequestValue(state, 'applicant.personalInfo.name.firstName'), shallowEqual)
  const email = useSelector((state: RootState) => getQuoteRequestValue(state, 'applicant.address.email'), shallowEqual)

  const [
    fullName,
    setFullName,
  ] = useState(firstName as Value)
  const [
    emailAddress,
    setEmailAddress,
  ] = useState(email as Value)
  const [
    phoneNumber,
    setPhoneNumber,
  ] = useState('' as Value)
  const [
    comments,
    setComments,
  ] = useState('' as Value)

  const { pushDataLayerEvent } = useGoogleTagManager()

  const onSubmit = useCallback(async () => {
    const error = validateForm({
      fullName,
      emailAddress,
      phoneNumber,
      comments,
    })

    if (error) {
      return Promise.reject(error)
    }

    setIsSubmitting(true)

    return post(`/quotes/personal-info/${insuranceType}`, {
      comments,
      emailAddress,
      fullName,
      insuranceType,
      phoneNumber,
    })
      .then((resp: ApiResponse) => {
        if (!resp.success) {
          setIsSubmitting(false)

          return Promise.reject(resp.firstErrorMessage)
        }

        pushDataLayerEvent({
          ...events.quote_requested,
          product_type: insuranceType,
        })

        setIsSubmitting(false)

        history.push(`/quote/new/${insuranceType}/personal-info/success`)

        return Promise.resolve(true)
      })
      .catch((error: string) => {
        setIsSubmitting(false)

        return Promise.reject(error || 'An error has occurred. Please try again.')
      })
  }, [
    history,
    pushDataLayerEvent,
    setIsSubmitting,
    fullName,
    emailAddress,
    phoneNumber,
    insuranceType,
    comments,
  ])

  return (
    <QuotingWrapper noPadding={true} theme={(insuranceType as string as QuotingWrapperTheme) || undefined}>
      <HeaderMeta
        title='Complete Your Information | Insurance Lounge'
        noIndex={true}
      />

      <Content center={true}>
        <Title level={3}>
          Thanks<UserSuppliedText>{(firstName && `, ${firstName}`) || undefined}</UserSuppliedText>.
          <br />
          Please complete your information so we can quote your {(insuranceType && `${insuranceType} `)}policy.
        </Title>
      </Content>
      <Content center={true} margined={true}>
        <Form short={true}>
          <InputGroup
            type={InputType.text}
            label='Full Name'
            name='fullName'
            onChange={value => setFullName(value)}
            value={fullName}
          />

          <InputGroup
            type={InputType.text}
            label='Email Address'
            name='email'
            onChange={value => setEmailAddress(value)}
            value={emailAddress}
          />

          <InputGroup
            type={InputType.text}
            label='Phone Number'
            name='phoneNumber'
            onChange={value => setPhoneNumber(value)}
            value={phoneNumber}
          />

          <InputGroup
            type={InputType.select}
            disabled={true}
            label='Insurance Product'
            name='insuranceType'
            options={[
              {
                label: 'Auto',
                value: 'auto',
              },
              {
                label: 'Home',
                value: 'home',
              },
            ]}
            renderOptions={insuranceTypes => insuranceTypes.map(insuranceType => {
              return (
                <SelectOption key={insuranceType.value} value={insuranceType.value}>
                  {insuranceType.label}
                </SelectOption>
              )
            })}
            value={insuranceType}
          />

          <InputGroup
            type={InputType.textArea}
            label='Comments'
            name='comments'
            onChange={value => setComments(value)}
            value={comments}
          />

          <ButtonRow>
            <Button
              center={true}
              wide={true}
              disabled={isSubmitting}
              onClick={(event: React.MouseEvent): void => {
                event.preventDefault()

                onSubmit()
                  .catch((error: string) => {
                    toast(error || 'An error has occurred. Please try again.')
                  })
              }}
            >
              {isSubmitting ? 'Sending...' : 'Next'}
            </Button>
          </ButtonRow>
        </Form>
      </Content>
    </QuotingWrapper>
  )
}

const validateForm = (values: {
  fullName?: Value,
  emailAddress?: Value,
  phoneNumber?: Value,
  comments?: Value,
}) => {
  const fields = [
    {
      name: 'fullName',
      rules: 'required|match:pattern=^[^ ]+ [^ ]+$',
      value: values.fullName,
      validationErrorMessage: 'Please enter a valid first and last name.',
    },
    {
      name: 'emailAddress',
      rules: 'required|email',
      value: values.emailAddress,
      validationErrorMessage: 'Please enter a valid email address.',
    },
    {
      name: 'phoneNumber',
      rules: 'required|phoneNumber',
      value: values.phoneNumber,
      validationErrorMessage: 'Please enter a valid phone number.',
    },
    {
      name: 'comments',
      rules: 'required',
      value: values.comments,
      validationErrorMessage: 'Please enter a comment.',
    },
  ]
    .map(field => ({
      ...field,
      validation: validate(field.rules, field.value),
    }))

  const firstInvalidField = fields.find(field => !field.validation.isValid)

  if (firstInvalidField) {
    return firstInvalidField.validationErrorMessage
  }

  return undefined
}

export default QuotingPersonalInfo
